<template>
  <modal
    :ctrl="ctrl"
    :positiveLabel="{
      text: flowRan ? $t('OK') : $t('RUN'),
      color: flowRan ? 'green' : 'red',
      icon: 'run',
      onclick: runFlow
    }"
    :negativeLabel="{ text: $t('CANCEL') }"
  >
    <div class="ui fitted basic segment">
      <div class="ui inverted dimmer" :class="{ active: flowRunning }">
        <div class="ui text loader">{{ $t('RUNNING_FLOW') }}</div>
      </div>
      <div class="ui two column grid" v-if="!(flowResult || flowError)">
        <!-- 1) Find flow to run -->
        <div class="column">
          <h4 class="ui header">{{ $t('ESTERO_FIND_FLOW') }}</h4>
          <div class="ui small fluid icon input">
            <input type="text" :placeholder="$t('SEARCH')" v-model="flowSearchTerm" />
            <i class="search icon"></i>
          </div>
          <div class="ui middle aligned relaxed selection scroller list" style="height: 200px;">
            <div
              class="item"
              :class="{ active: f.id === selectedFlowId }"
              v-for="f in filteredFlows"
              :key="f.id"
              @click="selectFlow(f)"
            >
              <div class="right floated content">
                {{ f.application }}
              </div>
              <div class="content">
                <div class="header">{{ f.name }}</div>
              </div>
            </div>
          </div>
        </div>
        <!-- 2) Specify inputs -->
        <div class="column">
          <h4 class="ui header">{{ $t('ESTERO_SPECIFY_INPUTS') }}</h4>
          <div class="ui middle aligned relaxed scroller list" style="height: 200px;">
            <div class="item" v-for="input in inputs" :key="input.id">
              <div class="right floated content">
                <div class="ui tiny input">
                  <input type="text" v-model="input.value" />
                </div>
              </div>
              <div class="content">
                <div class="header">
                  <div class="ui tiny input" v-if="input.editable">
                    <input type="text" v-model="input.name" />
                  </div>
                  <span style="padding-top:10px;" v-else>
                    {{ input.name }}
                  </span>
                </div>
              </div>
            </div>
            <div class="item">
              <div class="right floated content">
                <div
                  class="ui tiny green icon button"
                  :class="{ disabled: !selectedFlowId }"
                  @click="addInput"
                >
                  <i class="ui plus icon"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Show the result of running the flow -->
      <div v-else>
        <div class="ui center aligned basic segment" v-if="flowResult === true">
          <div class="ui icon centered header">
            <i class="ui green check icon"></i>
            {{ $t('ESTERO_FLOW_NO_RESULTS') }}
          </div>
        </div>
        <div class="ui center aligned basic segment" v-else-if="flowError">
          <div class="ui icon centered header">
            <i class="ui red sad tear outline icon"></i>
            {{ $t('ESTERO_FLOW_ERROR', { err: flowError }) }}
          </div>
        </div>
        <template v-else>
          <h4 class="ui header">{{ $t('ESTERO_FLOW_RESULTS') }}</h4>
          <div class="ui middle aligned relaxed scroller list" style="height: 200px;">
            <div class="item" v-for="(val, key) in flowResult" :key="key">
              <div class="right floated monospaced content">
                {{ val }}
              </div>
              <div class="content">
                <div class="header">
                  {{ key }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </modal>
</template>

<style lang="scss" scoped>
@charset "utf-8";

.scroller {
  overflow-y: scroll;
}
</style>

<script>
// @flow
import Vue from 'vue'
import fuzzy from 'fuzzy'
import { Estero, Flow, Application } from 'services'
import { Modal, ModalController } from 'ui'

export default {
  name: 'run-flow',

  components: { Modal },

  props: ['ctrl', 'manatee'],

  data() {
    return {
      flowSearchTerm: null,
      selectedFlowId: '',
      selectedFlowSubject: '',
      inputs: [],
      flowResult: null,
      flowError: null,
      flowRunning: false,
      flowRan: false
    }
  },

  computed: {
    filteredFlows() {
      return fuzzy
        .filter(this.flowSearchTerm, this.flows, { extract: f => f.name })
        .slice(0, 10)
        .map(hit => {
          if (hit.original) {
            return {
              id: hit.original.id,
              name: hit.original.name,
              subject: hit.original.typeConfig ? hit.original.typeConfig.subjectPrefix || '' : '',
              inputs: _.map(hit.original.inputs, i => ({ id: i.name, ...i })),
              application: this.appMap[hit.original.application].name
            }
          }
          return {}
        })
    },
    flows() {
      return this.$store.state.flow.items
    },
    appMap() {
      return _.keyBy(this.apps, 'id')
    },
    apps() {
      return this.$store.state.application.items
    }
  },

  methods: {
    addInput() {
      this.inputs.push({ id: `${Math.round(Math.random() * 1000)}`, name: '', editable: true })
    },
    selectFlow(f) {
      this.selectedFlowId = f.id
      this.selectedFlowSubject = f.subject
      this.inputs = f.inputs
    },

    runFlow() {
      if (this.flowRan) {
        this.flowRan = false
        this.flowResult = null
        this.flowError = null
        this.flowSearchTerm = null
        return true
      }

      const inputsAsObj = _.reduce(
        this.inputs,
        (inputs, i) => {
          inputs[i.name] = i.value
          return inputs
        },
        {}
      )
      this.flowRunning = true
      // Execute the flow
      Estero.run(this.manatee, this.selectedFlowId, this.selectedFlowSubject, inputsAsObj)
        .then(r => (this.flowResult = r.data || true))
        .catch(err => {
          this.flowError = err
        })
        .finally(() => {
          this.flowRunning = false
          this.flowRan = true
        })
      return false
    }
  },

  mounted() {
    // Make sure all flows and applications are available
    Flow.readAllFlows()
    Application.readAllApps()
  }
}
</script>
